import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { calculateBarHeight, formatChartValue, formatCurrencyValue, getChartColorClass } from 'src/app/utils/format.utils';
import { CommonModule } from '@angular/common';

export interface BarChartItem {
  label: string;
  value: number;
}

@Component({
  selector: 'app-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss']
})
export class BarChartComponent implements OnInit, OnChanges {
  @Input() data: BarChartItem[] = [];
  @Input() title: string = '';
  @Input() formatValues: boolean = true;
  @Input() currencyFormat: boolean = false;
  @Input() showLegend: boolean = true;
  @Input() animated: boolean = true;
  
  private maxValue: number = 100;
  public gridValues: number[] = [];
  
  constructor() { }
  
  ngOnInit(): void {
    this.calculateMaxValue();
    this.calculateGridValues();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.calculateMaxValue();
      this.calculateGridValues();
    }
  }
  
  /**
   * Calcula o valor máximo entre os itens do gráfico
   * Usado como referência para o cálculo da altura das barras
   */
  private calculateMaxValue(): void {
    if (!this.data || this.data.length === 0) {
      this.maxValue = 100;
      return;
    }
    
    // Define o valor máximo com base nos valores dos dados
    const values = this.data
      .filter(item => item && typeof item.value === 'number' && !isNaN(item.value))
      .map(item => item.value);
    
    let calculatedMax = 0;
    if (values.length > 0) {
      calculatedMax = Math.max(...values);
      
      // Ajuste: Reduzimos ligeiramente o valor máximo para que a barra mais alta
      // não ocupe 100% do espaço, dando mais espaço visual para as barras menores
      calculatedMax = calculatedMax * 1.1; // Adiciona 10% ao valor máximo
    }
    
    // Se o máximo for zero ou muito pequeno, define um valor padrão
    this.maxValue = calculatedMax <= 0 ? 100 : calculatedMax;
  }
  
  /**
   * Calcula os valores para as linhas do grid
   * Divide o valor máximo em 5 partes para criar valores para o grid
   * com valores arredondados para múltiplos de 10
   */
  private calculateGridValues(): void {
    this.gridValues = [];
    const steps = 5;
    
    // Adicionamos 5% ao valor máximo para dar um pequeno espaço no topo do gráfico
    let adjustedMaxValue = this.maxValue * 1.05;
    
    // Arredondar para o próximo múltiplo de 10 mais alto
    adjustedMaxValue = Math.ceil(adjustedMaxValue / 10) * 10;
    
    // Calcular o valor de cada passo (garantindo que seja múltiplo de 10)
    const stepValue = Math.ceil(adjustedMaxValue / steps / 10) * 10;
    
    // Recalcular o valor máximo ajustado baseado no tamanho do passo
    adjustedMaxValue = stepValue * steps;
    
    // Gerar os valores da grade (em ordem decrescente)
    for (let i = steps; i >= 0; i--) {
      const value = stepValue * i;
      this.gridValues.push(value);
    }
  }
  
  /**
   * Formata o valor para exibição no grid
   */
  formatGridValue(value: number): string {
    if (this.currencyFormat) {
      // Para valores monetários
      if (value >= 1000000) {
        const millions = value / 1000000;
        return 'R$ ' + millions.toLocaleString('pt-BR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }) + 'M';
      }
      
      if (value >= 1000) {
        const thousands = value / 1000;
        return 'R$ ' + thousands.toLocaleString('pt-BR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1
        }) + 'K';
      }
      
      // Para valores menores que 1000
      return 'R$ ' + Math.round(value).toLocaleString('pt-BR');
    }
    
    // Para valores normais, exibir apenas o inteiro
    return Math.round(value).toString();
  }
  
  /**
   * Obtém a altura da barra com base no valor
   * Garante que mesmo valores pequenos sejam visíveis
   */
  getBarHeight(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '0px'; // Barras sem valor não são exibidas
    }
    
    // Usa diretamente o valor máximo do grid para calcular a altura,
    // garantindo que os valores correspondam exatamente às linhas de grade
    const maxGridValue = this.gridValues[0];
    
    // Calcula a altura proporcional usando o valor máximo do grid
    return calculateBarHeight(value, maxGridValue);
  }
  
  /**
   * Formata o valor para exibição
   */
  formatValue(value: number): string {
    if (!this.formatValues) {
      return value.toString();
    }
    
    // Se a formatação monetária estiver ativada, usa o formato de moeda
    if (this.currencyFormat) {
      return formatCurrencyValue(value);
    }
    
    // Caso contrário, usa a formatação padrão
    return formatChartValue(value);
  }
  
  /**
   * Obtém a classe de cor para a barra baseada no índice
   */
  getColorClass(index: number): string {
    return getChartColorClass(index);
  }

  /**
   * Função de tracking para otimizar a renderização da lista
   */
  trackByIndex(index: number, item: any): number {
    return index;
  }
} 